<template>
  <app-card-info card-title="Offer Details" class="auction-info">
    <div class="auction-info__status">
      Status:
      <app-asset-status :status="getStatus" class="ml-2" />
      <slot name="biddersTable"></slot>
      <app-asset-progress
        v-if="!isPaused && !isAuctionCancelled && !isAuctionCompleted"
        class="mt-10"
      />
      <div class="mt-4">
        <!-- Cancelled Auction Alert -->
        <div v-if="isAuctionCancelled" class="cancelled-banner">
          <div class="cancelled-banner__icon">✕</div>
          <div class="cancelled-banner__content">
            <div class="cancelled-banner__title">Auction Cancelled</div>
            <div class="cancelled-banner__subtitle">
              This auction has been cancelled. Bidding is no longer available.
            </div>
          </div>
        </div>
        <!-- Completed Auction Alert -->
        <div v-else-if="isAuctionCompleted" class="completed-banner">
          <div class="completed-banner__icon">✓</div>
          <div class="completed-banner__content">
            <div class="completed-banner__title">Auction Completed</div>
            <div class="completed-banner__subtitle">
              This auction has ended. Bidding is no longer available.
            </div>
          </div>
        </div>
        <!-- Paused Auction Alert -->
        <div v-else-if="isPaused" class="paused-banner">
          <div class="paused-banner__icon">⏸️</div>
          <div class="paused-banner__content">
            <div class="paused-banner__title">Auction Paused</div>
            <div class="paused-banner__subtitle">
              Bidding is temporarily suspended
            </div>
          </div>
        </div>
        <!-- Normal Auction Content (hidden when cancelled/completed/paused) -->
        <template
          v-if="!isAuctionCancelled && !isAuctionCompleted && !isPaused"
        >
          <span>{{ getEndTitle }}</span>
          <app-asset-timer :end-date="getEndDate" class="auction-info__timer" />
        </template>
      </div>
    </div>
  </app-card-info>
</template>

<script setup lang="ts">
import AppAssetStatus from '@/components/App/AppAssetStatus.vue';
import AppCardInfo from '@/components/App/AppCardInfo.vue';
import AppAssetProgress from '@/components/App/AppAssetProgress.vue';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { IAsset } from '@/types/store';
import AppAssetTimer from '@/components/App/AppAssetTimer.vue';
import { RAW_ASSETS_STATUSES } from '@/constants/statuses';

// #constants
const { getters } = useStore();

// #computed
const asset = computed<IAsset>(() => {
  return getters['getAsset'];
});
const getStatus = computed<string>(() => {
  const { status } = asset.value ?? {};
  return status;
});
const getEndDate = computed<string>(() => {
  const { auction } = asset.value ?? {};
  const { endDate = '' } = auction ?? {};
  return endDate;
});
const isAuctionCancelled = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.CANCELLED;
});
const isAuctionCompleted = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.COMPLETED;
});
const isPaused = computed<boolean>(() => {
  // Don't show paused if auction is cancelled or completed
  if (isAuctionCancelled.value || isAuctionCompleted.value) {
    return false;
  }
  const { auction } = asset.value ?? {};
  return !!(auction as any)?.isPaused;
});
const isLiveAuction = computed<boolean>(() => {
  return RAW_ASSETS_STATUSES.LIVE_AUCTION === getStatus.value;
});
const getEndTitle = computed<string>(() => {
  return isLiveAuction.value
    ? 'Live-auction ends in:'
    : 'Offer submission ends in:';
});
</script>

<style scoped lang="scss">
.auction-info {
  padding-bottom: 24px !important;
  &__status {
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
  &__timer {
    width: 100%;
    max-width: 300px;
    margin-left: -10px;
  }
}

.paused-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-left: 4px solid #d97706;
  border-radius: 10px;
  animation: pulse-paused 2s ease-in-out infinite;
  margin-top: 8px;

  &__icon {
    font-size: 24px;
    animation: bounce-icon 1s ease-in-out infinite;
  }

  &__content {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #92400e;
    letter-spacing: 0.3px;
  }

  &__subtitle {
    font-size: 13px;
    color: #a16207;
    margin-top: 2px;
  }
}

@keyframes pulse-paused {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.2);
  }
  50% {
    box-shadow: 0 4px 16px rgba(217, 119, 6, 0.35);
  }
}

@keyframes bounce-icon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.cancelled-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 1px solid #fca5a5;
  border-left: 4px solid #ef4444;
  border-radius: 10px;
  animation: fadeIn 0.3s ease-out;
  margin-top: 8px;

  &__icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ef4444;
    color: white;
    border-radius: 50%;
    font-size: 18px;
    font-weight: bold;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #991b1b;
    letter-spacing: 0.3px;
  }

  &__subtitle {
    font-size: 13px;
    color: #b91c1c;
    margin-top: 2px;
    line-height: 1.5;
  }
}

.completed-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1px solid #6ee7b7;
  border-left: 4px solid #10b981;
  border-radius: 10px;
  animation: fadeIn 0.3s ease-out;
  margin-top: 8px;

  &__icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #10b981;
    color: white;
    border-radius: 50%;
    font-size: 18px;
    font-weight: bold;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #065f46;
    letter-spacing: 0.3px;
  }

  &__subtitle {
    font-size: 13px;
    color: #047857;
    margin-top: 2px;
    line-height: 1.5;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
